import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Divider,
  useToast,
  Select,
  Checkbox,
  CheckboxGroup,
  Stack,
  Input,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { FiExternalLink, FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import keycloakClient from '../Keycloak';
import { apiService } from '../ExternalApiService';
import Pagination from '../components/Pagination';
import { MotionVStack, MotionGrid, MotionCard, MotionBox, variants } from '../components/motion';

const ALL_STATUSES = ['pending', 'paid', 'failed', 'cancelled'];

const Billing = () => {
  const toast = useToast();
  const GCT = 0.15;
  const { email, name } = keycloakClient.tokenParsed || {};

  const showToast = (status, title, description = '') => {
    toast({
      title,
      description,
      status,
      duration: 5000,
      isClosable: true,
      position: 'top',
    });
  };

  const calculatePrice = (credits) => {
    const price = process.env.REACT_APP_CREDIT_COST;
    const unitCost = parseFloat(price ?? '1.4'); // dollar cost per credit
    const partial = credits * unitCost;
    const total = partial + partial * GCT;
    return Math.round(total); // cents
  };

  const packages = [
    { id: '1000', name: 'Starter Package', messages: 1000, price: calculatePrice(1000), currency: 'JMD', popular: false },
    { id: '1500', name: 'Professional Package', messages: 1500, price: calculatePrice(1500), currency: 'JMD', popular: true },
    { id: '2000', name: 'Enterprise Package', messages: 2000, price: calculatePrice(2000), currency: 'JMD', popular: false },
  ];

  const formatCurrency = (amount, currency = 'JMD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const getUsagePercentage = () => (usageSummary?.currentUsage / usageSummary?.totalCredits) * 100;

  const submitPayment = async (credits, price) => {
    try {
      const data = await apiService.createPaymentOrder(credits, price);
      if (!data.custom_token) throw new Error('Missing payment token');

      const fields = {
        order_id: data.order_id,
        token: data.custom_token,
        platform: 'custom',
        email_address: email,
        customer_name: name,
        currency: 'JMD',
        amount: price,
      };

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://secure-test.ezeepayments.com';
      form.style.display = 'none';
      form.acceptCharset = 'UTF-8';

      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = String(value ?? '');
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      setTimeout(() => form.remove(), 0);
    } catch (error) {
      console.error('Error submitting payment:', error);
      showToast('error', 'Failed to submit payment', 'Please try again later.');
    }
  };

  // -----------------------------
  // Payment History: state + fetch
  // -----------------------------
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, per_page: 10, total: 0, last_page: 1 });
  const [limit, setLimit] = useState()
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [usageSummary, setUsageSummary] = useState({});
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [status, setStatus] = useState([]); // array of strings
  const [from, setFrom] = useState('');     // YYYY-MM-DD
  const [to, setTo] = useState('');

  // prevent race conditions on overlapping fetches
  const inFlight = useRef(0);

  const fetchHistory = useCallback(async (overrides = {}) => {
    setLoading(true);
    setErrMsg('');
    inFlight.current += 1;
    const ticket = inFlight.current;

    try {
      const payload = {
        page: overrides.page ?? page,
        take: overrides.take ?? take,
        status: overrides.status ?? status,
        from: (overrides.from ?? from) || undefined,
        to: (overrides.to ?? to) || undefined,
      };

      const { data, meta } = await apiService.fetchPaymentHistory(payload);
      if (ticket !== inFlight.current) return; // stale response

      setRows(Array.isArray(data) ? data : []);
      setMeta(meta ?? { current_page: payload.page, per_page: payload.take, total: 0, last_page: 1 });
    } catch (e) {
      if (ticket !== inFlight.current) return;
      const msg = e?.message || 'Failed to load billing history';
      setErrMsg(msg);
      showToast('error', 'Billing history error', msg);
    } finally {
      if (ticket === inFlight.current) setLoading(false);
    }
  }, [page, take, status, from, to, limit]);

  const fetchUsageSummary = useCallback(async () => {
    try {
      const data = await apiService.getUsageSummary();
      setUsageSummary(data);
    } catch (e) {
      console.error(e)
      showToast('error', 'Failed to fetch usage')
    }
  }, []);

  // auto fetch on input change
  useEffect(() => {
    fetchHistory();
    fetchUsageSummary();
  }, [page, take, status, from, to, fetchHistory, limit]);

  const canPrev = meta.current_page > 1;
  const canNext = meta.current_page < meta.last_page;

  const resetToFirstPage = () => setPage(1);

  const statusSet = useMemo(() => new Set(status), [status]);
  const toggleStatus = (s) => {
    const next = new Set(statusSet);
    if (next.has(s)) next.delete(s); else next.add(s);
    setStatus(Array.from(next));
    resetToFirstPage();
  };

  const refresh = () => fetchHistory({});

  const amountFromCents = (cents) =>
    (Number(cents ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const statusColor = (s) => {
    switch ((s || '').toLowerCase()) {
      case 'paid': return 'green';
      case 'pending': return 'yellow';
      case 'failed': return 'red';
      case 'refunded': return 'purple';
      case 'cancelled': return 'gray';
      default: return 'blue';
    }
  };

  return (
    <MotionVStack spacing={6} align="stretch" variants={variants.container} initial="hidden" animate="show">
      <MotionBox variants={variants.item}>
        <Heading size="lg">Billing & Usage</Heading>
        <Text color="gray.600" mt={2}>
          Manage your subscription and view usage statistics
        </Text>
      </MotionBox>

      <MotionCard variants={variants.item}>
        <CardHeader>
          <Heading size="md">Current Usage</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4}>
            <HStack justify="space-between" w="full">
              <Text>Messages Used</Text>
              <Text fontWeight="bold">{usageSummary?.currentUsage} / {usageSummary?.totalCredits}</Text>
            </HStack>
            <Box w="full" bg="gray.200" rounded="full" h="8px">
              <Box
                bg="blue.500"
                h="8px"
                rounded="full"
                w={`${getUsagePercentage()}%`}
                transition="width 0.3s ease"
              />
            </Box>
            <HStack justify="space-between" w="full">
              <Text fontSize="sm" color="gray.600">Remaining</Text>
              <Text fontSize="sm" color="gray.600">{usageSummary?.remainingCredits} messages</Text>
            </HStack>
          </VStack>
        </CardBody>
      </MotionCard>

      <MotionCard variants={variants.item}>
        <CardHeader>
          <Heading size="md">Purchase Messages</Heading>
        </CardHeader>
        <CardBody>
          <MotionGrid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} variants={variants.container}>
            {packages.map((pkg) => (
              <MotionCard
                key={pkg.id}
                variant="outline"
                borderColor={pkg.popular ? 'blue.500' : 'gray.200'}
                position="relative"
                variants={variants.item}
              >
                {pkg.popular && (
                  <Badge position="absolute" top="-10px" left="50%" transform="translateX(-50%)" colorScheme="blue">
                    Most Popular
                  </Badge>
                )}
                <CardBody>
                  <VStack spacing={4}>
                    <Heading size="sm">{pkg.name}</Heading>
                    <Text fontSize="2xl" fontWeight="bold">
                      {formatCurrency(pkg.price, pkg.currency)}
                    </Text>
                    <Text color="gray.600">{pkg.messages} Messages</Text>
                    <Button
                      colorScheme={pkg.popular ? 'blue' : 'gray'}
                      variant={pkg.popular ? 'solid' : 'outline'}
                      w="full"
                      onClick={() => submitPayment(pkg.messages, pkg.price)}
                      rightIcon={<FiExternalLink />}
                    >
                      Purchase
                    </Button>
                  </VStack>
                </CardBody>
              </MotionCard>
            ))}
          </MotionGrid>
        </CardBody>
      </MotionCard>

      {/* Billing History */}
      <MotionCard variants={variants.item}>
        <CardHeader>
          <HStack justify="space-between" align="center">
            <Heading size="md">Billing History</Heading>
            <HStack>
              <IconButton
                aria-label="Refresh"
                icon={<FiRefreshCw />}
                onClick={refresh}
                isDisabled={loading}
                variant="ghost"
              />
            </HStack>
          </HStack>
        </CardHeader>
        <CardBody>
          {/* Filters */}
          <HStack spacing={4} align="start" justify="space-between" mb={4}>
            <HStack spacing={4} align="center" wrap="wrap">
              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>Per page</Text>
                <Select
                  value={take}
                  onChange={(e) => { setTake(Number(e.target.value)); resetToFirstPage(); }}
                  w="120px"
                >
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>From</Text>
                <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); resetToFirstPage(); }} />
              </Box>

              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>To</Text>
                <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); resetToFirstPage(); }} />
              </Box>
            </HStack>

            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>Status</Text>
              <CheckboxGroup value={status} onChange={(vals) => { setStatus(vals); resetToFirstPage(); }}>
                <Stack direction="row" spacing={4} wrap="wrap">
                  {ALL_STATUSES.map(s => (
                    <Checkbox key={s} value={s} onChange={() => toggleStatus(s)}>
                      {s.toUpperCase()}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            </Box>
          </HStack>

          <Divider orientation="horizontal" h={2} borderColor="gray.300" />

          {/* List */}
          <VStack spacing={3} align="stretch" marginTop={12}>
            {loading && (
              <HStack justify="center" py={6}>
                <Spinner />
                <Text>Loading payments…</Text>
              </HStack>
            )}

            {!loading && errMsg && (
              <Box color="red.500" fontSize="sm">{errMsg}</Box>
            )}

            {!loading && !errMsg && rows.length === 0 && (
              <Box color="gray.600" fontSize="sm">No payments found.</Box>
            )}

            {!loading && rows.map((p, index) => (
              <Box key={p.order_id || `${p.email}-${index}`}>
                <HStack justify="space-between" align="center">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium">
                      {p.credits ?? '—'} credits — {p.currency ?? 'JMD'} {amountFromCents(p.amount_cents)}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {p.paid_at
                        ? new Date(p.paid_at).toLocaleString([], {
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })
                        : 'Created: ' + (
                          p.created_at
                            ? new Date(p.created_at).toLocaleString([], {
                              month: 'short',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })
                            : '—'
                        )
                      }
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Order: {p.order_id ?? '—'}
                    </Text>
                  </VStack>
                  <HStack spacing={4}>
                    <Text fontWeight="bold">{formatCurrency(Number(p.amount_cents ?? 0), p.currency ?? 'JMD')}</Text>
                    <Badge colorScheme={statusColor(p.status)} variant="subtle" textTransform="capitalize">
                      {p.status ?? 'unknown'}
                    </Badge>
                  </HStack>
                </HStack>
                {index < rows.length - 1 && <Divider mt={3} />}
              </Box>
            ))}
          </VStack>

          <Pagination 
            currentPage={page}
            pageSize={meta.per_page}
            totalItems={meta?.total ?? 0}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(size) => { setPage(1); setLimit(size); }}
            pageSizeOptions={[10, 25, 50]}
            isLoading={loading}
          />
        </CardBody>
      </MotionCard>
    </MotionVStack>
  );
};

export default Billing;
